import clsx from 'clsx'
import { JourneyStepType } from '../../../types'
import { statIcons, stepCategoryColors } from './JourneyEditorUtils'
import { Alert, Menu, MenuItem } from '../../../ui'
import { createElement, SetStateAction, useContext } from 'react'
import { JourneyContext, ProjectContext } from '../../../contexts'
import { useTranslation } from 'react-i18next'
import TextInput from '../../../ui/form/TextInput'
import { Node } from 'reactflow'

interface JourneyStepEditProps {
    editNode: Node<any, string | undefined>
    handleSetNodes: (nodes: SetStateAction<Array<Node<any, string | undefined>>>) => void
    nodes: Array<Node<any, string | undefined>>
    setViewUsersStep: (step: { stepId: number, stepType: string }) => void
    type: JourneyStepType<any, any>
    disabled?: boolean
}

export const JourneyStepEdit = ({
    editNode,
    type,
    setViewUsersStep,
    handleSetNodes,
    nodes,
    disabled,
}: JourneyStepEditProps) => {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const [journey] = useContext(JourneyContext)

    const stats = editNode.data.stats ?? {}

    return (
        <div className="journey-options">
            <div className="journey-step-header">
                <span className={clsx('step-header-icon', stepCategoryColors[type.category])}>
                    {type.icon}
                </span>
                <h4 className="step-header-title">{t(type.name)}</h4>
                <div
                    className="step-header-stats"
                    role={editNode.data.stepId ? 'button' : undefined}
                    onClick={editNode.data.stepId
                        ? () => setViewUsersStep({ stepId: editNode.data.stepId, stepType: editNode.data.type })
                        : undefined
                    }
                    style={{
                        cursor: editNode.data.stepId ? 'cursor' : undefined,
                    }}
                >
                    <span className="stat">
                        {(stats.completed ?? 0).toLocaleString()}
                        {statIcons.completed}
                    </span>
                    {
                        (editNode.data.type === 'delay' || !!stats.delay) && (
                            <span className="stat">
                                {stats.delay ?? 0}
                                {statIcons.delay}
                            </span>
                        )
                    }
                    {
                        (editNode.data.type === 'action' || !!stats.action) && (
                            <span className="stat">
                                {stats.action ?? 0}
                                {statIcons.action}
                            </span>
                        )
                    }
                </div>
                <Menu size="small">
                    <MenuItem onClick={() => handleSetNodes(nds => nds.filter(item => item.id !== editNode.id))
                    }>{t('delete_step')}</MenuItem>
                </Menu>
            </div>
            <div className="journey-options-edit" style={{ maxWidth: type.maxWidth ?? 500 }}>

                {disabled && <Alert size="small" title={t('journey_draft_step_edit_disabled_title')}>
                    {t('journey_draft_step_edit_disabled_desc')}
                </Alert>}
                <TextInput
                    label={t('name')}
                    name="name"
                    value={editNode.data.name ?? ''}
                    onChange={name => handleSetNodes(nds => nds.map(n => n.id === editNode.id ? { ...n, data: { ...n.data, name } } : n))}
                    disabled={disabled}
                />
                {
                    type.hasDataKey && (
                        <TextInput
                            label={t('data_key')}
                            subtitle={t('data_key_description')}
                            name="data_key"
                            value={editNode.data.data_key}
                            onChange={data_key => handleSetNodes(nds => nds.map(n => n.id === editNode.id ? { ...n, data: { ...n.data, data_key } } : n))}
                            disabled={disabled}
                        />
                    )
                }
                {
                    type.Edit && createElement(type.Edit, {
                        value: editNode.data.data ?? {},
                        onChange: data => handleSetNodes(nds => nds.map(n => n.id === editNode.id
                            ? {
                                ...editNode,
                                data: {
                                    ...editNode.data,
                                    data,
                                },
                            }
                            : n,
                        )),
                        project,
                        journey,
                        stepId: editNode.data.stepId,
                        nodes,
                        disabled,
                    })
                }
            </div>
        </div>
    )
}
